import { List, Map } from 'immutable';
import { isEmpty } from 'lodash';

import { createCache } from '../configs/$global';
import {
  getGridLineSlotFamilyConf,
  getGridLineSlotNameConf,
  getGridLineSlotSequencingConf,
} from '../configs/gridSlotTypes';
import { getMatchGridTemplateByName } from '../configs/matchGridTemplates';
import { SupportedLang } from '../constants/locales';
import {
  BALANCING_FUNCTION_MAP,
  SLOT_FAMILY_SINGLE,
} from '../constants/matchGridStakes';
import {
  TWO_WAY_STAKE,
} from '../constants/StakeRenderer';
import { getLineNameByLang } from './betting/get-line-name-by-lang';
import { RecordOfSlotEntryType, SlotEntry } from './SlotEntry';

export const SPORT_LINE_KIND = {
  main: 'main',
  regular: 'regular',
  additional: 'additional',
};

/**
 * @private
 */
const $cache = createCache('getStakeTypeGroupConf');

// --

/**
 * @typedef {object} StakeTypeGroupConf
 * @property {{ru: string, en: string}} names
 */

/**
 * Get stake type group configuration by id.
 *
 * @param {number} groupId
 * @return {StakeTypeGroupConf}
 */
export function getStakeTypeGroupConf(groupId) {
  const $stakeTypeGroupsCache = $cache('stake-type-groups');

  if (!(groupId in $stakeTypeGroupsCache)) {
    $stakeTypeGroupsCache[groupId] = (getMatchGridTemplateByName('STAKE_TYPE_GROUP_CONF')[groupId] || {});
  }

  return $stakeTypeGroupsCache[groupId];
}

/**
 * @typedef {object} LineStakeType
 * @property {number} lineId
 * @property {number[]} stakeTypes
 */

/**
 * Get line stake type configuration by sport id.
 *
 * @param {number} sportId
 * @return {LineStakeType[]}
 */

export function getLineStakeTypeConf(sportId) {
  const $lineStakeTypeGroupCache = $cache('line-stake-type');

  if (!(sportId in $lineStakeTypeGroupCache)) {
    $lineStakeTypeGroupCache[sportId] = (getMatchGridTemplateByName('LINE_STAKE_TYPE_CONF')[sportId] || []);
  }

  return $lineStakeTypeGroupCache[sportId];
}

/**
 * @typedef {object} LineStakeTypeGroup
 * @property {number} lineId
 * @property {number} groupId
 * @property {number} stakeType
 * @property {{ period: number } | undefined} filter
 */

/**
 * Get line stake type group configuration by sport id.
 *
 * @param {number} sportId
 * @return {SportLinesStakeType[]}
 */
export function getLineStakeTypeGroupConf(sportId) {
  const $lineStakeTypeGroupCache = $cache('line-stake-type-group');

  if (!(sportId in $lineStakeTypeGroupCache)) {
    $lineStakeTypeGroupCache[sportId] = (getMatchGridTemplateByName('LINE_STAKE_TYPE_GROUP_CONF')[sportId] || []);
  }

  return $lineStakeTypeGroupCache[sportId];
}

/**
 * Get Live/Prematch stake type groups title.
 *
 * @param {number} groupId
 * @param {SupportedLang} lang
 * @return {string}
 */
export const getStakeTypeGroupTitleConf = (groupId, lang) => {
  const confNames = getStakeTypeGroupConf(groupId).names;

  return getLineNameByLang(confNames, lang);
};

// --

/**
 * Get stake type group ids presence (+ordering) for a given `sportId`.
 *
 * @param {number} sportId
 * @return {number[]}
 */
export function getStakeTypeGroupIdsConf(sportId) {
  const $stakeTypeGroupIdsConf = $cache('stake-type-group-ids');
  const STAKE_TYPE_GROUP_BY_SPORT_CONF = getMatchGridTemplateByName('STAKE_TYPE_GROUP_BY_SPORT_CONF');

  $stakeTypeGroupIdsConf[sportId] = STAKE_TYPE_GROUP_BY_SPORT_CONF?.default || [];

  if (STAKE_TYPE_GROUP_BY_SPORT_CONF !== undefined && sportId in STAKE_TYPE_GROUP_BY_SPORT_CONF) {
    $stakeTypeGroupIdsConf[sportId] = $stakeTypeGroupIdsConf[sportId].concat(
      STAKE_TYPE_GROUP_BY_SPORT_CONF[sportId],
    );
  }

  return $stakeTypeGroupIdsConf[sportId];
}

export function getOrderedStakeTypeGroupIdsConf(sportId) {
  const $orderedStakeTypeGroupIds = $cache('stake-type-group-ids');

  if (!(sportId in $orderedStakeTypeGroupIds)) {
    const stakeTypeGroupIds = getStakeTypeGroupIdsConf(sportId);
    const priorityMap = getMatchGridTemplateByName('STAKE_TYPE_GROUP_PRIORITIES_CONF');

    for (const groupId of stakeTypeGroupIds) {
      if (!(groupId in priorityMap)) {
        priorityMap[groupId] = -groupId; // fallback priority value
      }
    }

    $orderedStakeTypeGroupIds[sportId] = (
      stakeTypeGroupIds
        .sort((a, b) => priorityMap[b] - priorityMap[a])
    );
  }

  return $orderedStakeTypeGroupIds[sportId];
}

// --

/**
 * @typedef {object} SportLineConf
 * @property {number} id
 * @property {number} index
 * @property {{ru: string, en: string}} names
 * @property {boolean} isMain
 * @property {'main' | 'regular' | 'additional'} kind
 */

/**
 * @private
 * @param {number} sportId
 * @return {SportLineConf}
 */
const buildFallbackSportLineConf = (sportId) => ({
  id: sportId * 1000,
  index: 0,
  names: {
    ru: 'Роспись',
    en: 'Additional',
  },
  isMain: true,
  kind: SPORT_LINE_KIND.additional,
  stakeTypes: [],
});

/**
 * Get lines configuration for a given `sportId`.
 *
 * If no sport-specific configuration is available, it returns
 * the fallback configuration with a single main line entry.
 *
 * @param {number} sportId
 * @return {SportLineConf[]}
 */
export function getSportLinesConf(sportId) {
  const $sportLinesConf = $cache('sport-lines');

  if (!(sportId in $sportLinesConf)) {
    const sportLinesConf = getMatchGridTemplateByName('SPORT_LINES_CONF')[sportId] || [];

    sportLinesConf.sort((a, b) => (a.index - b.index));

    if (!sportLinesConf.find((lineConf) => lineConf.kind === SPORT_LINE_KIND.additional)) {
      sportLinesConf.push(buildFallbackSportLineConf(sportId));
    }

    $sportLinesConf[sportId] = sportLinesConf;
  }

  return $sportLinesConf[sportId];
}

/**
 * Get the main line `id` for a given `sportId`.
 *
 * @param {number} sportId
 * @return {number | null}
 */
export function getSportMainLineIdConf(sportId) {
  const $mainLineIdConf = $cache('sport-main-line');

  if (!(sportId in $mainLineIdConf)) {
    const mainLine = getSportLinesConf(sportId).find((lineConf) => lineConf.isMain);

    if (mainLine) {
      $mainLineIdConf[sportId] = mainLine.id;
    }
    else {
      return null;
    }
  }

  return $mainLineIdConf[sportId];
}

/**
 * Get the additional line `id` for a given `sportId`.
 *
 * @param {number} sportId
 * @return {number}
 */
export function getSportAdditionalLineIdConf(sportId) {
  const $additionalLineIdConf = $cache('sport-additional-line');

  if (!(sportId in $additionalLineIdConf)) {
    $additionalLineIdConf[sportId] = getSportLinesConf(sportId).find((lineConf) => (
      lineConf.kind === SPORT_LINE_KIND.additional
    )).id;
  }

  return $additionalLineIdConf[sportId];
}

// --

/**
 * @typedef {object} SportLineStakeTypesConf
 * @property {number} lineId
 * @property {(number | { stakeType: number, filter: { period: number} })[]} stakeTypes
 */

/**
 * Get lines with stake types configuration for a given `sportId`.
 *
 * @param {number} sportId
 * @return {SportLineStakeTypesConf[]}
 */
export function getSportLinesStakeTypesConf(sportId) {
  const $sportLinesStakeTypesConf = $cache('sport-lines-stake-types');
  const LINE_STAKE_TYPE_CONF = getMatchGridTemplateByName('LINE_STAKE_TYPE_CONF');

  if (
    LINE_STAKE_TYPE_CONF !== undefined && (
      !(sportId in $sportLinesStakeTypesConf) || isEmpty($sportLinesStakeTypesConf[sportId])
    )
  ) {
    $sportLinesStakeTypesConf[sportId] = LINE_STAKE_TYPE_CONF[sportId] || [];
  }

  return $sportLinesStakeTypesConf[sportId];
}

// --

/**
 * Get match line renderer configuration.
 * It returns stake renderer type (TWO_WAY_STAKE, THREE_WAY_STAKE) depending on a given `stakeType`.
 *
 * @param {number} stakeType
 * @return {number}
 */
export function getStakeRendererConf(stakeType) {
  const $stakeRendererConf = $cache('stake-renderer');

  if (!(stakeType in $stakeRendererConf)) {
    const STAKE_RENDERER_CONF = getMatchGridTemplateByName('STAKE_RENDERER_CONF');

    /**
     * @type {string|undefined} key - key of `STAKE_RENDERER_CONF`, whose value is an array including given `stakeType`
     */
    const key = Object.keys(STAKE_RENDERER_CONF)
      .find((key) => STAKE_RENDERER_CONF[key].includes(stakeType));

    // Since `getStakeRendererConf` returns number, we must convert founded `key` to number
    $stakeRendererConf[stakeType] = key ? parseInt(key) : TWO_WAY_STAKE;
  }

  return $stakeRendererConf[stakeType];
}

// --

/**
 * This function receives a list of stakes
 * transform ids from string to number
 * compare number and returns the smallest as it is the earliest stake added
 *
 * @param {Immutable.Map<number, Stake>} stakes
 * @return {number|null} stake id if any
 */
export const defaultStakeTypeBalancer = (stakes) => {
  if (stakes.size === 0) {
    return null;
  }

  if (stakes.size === 1) {
    return stakes.first().id;
  }

  const firstStake = stakes.sort((firstStake, secondStake) => (
    firstStake.id - secondStake.id
  )).first();

  return firstStake.id;
};

/**
 * @callback BalancerFunction
 * @param {Immutable.Map<number, Stake>} stakes
 * @param {Immutable.Map<number, Bet>} bets
 * @returns {number | null}
 */

/**
 * Get balancing function by `stakeType`.
 *
 * @param {number} stakeType
 * @return {BalancerFunction}
 */
export const getBalancingFunctionByStakeType = (stakeType) => {
  const $balancingFunctionsByStakeType = $cache('stake-type-balancing-function');

  if (!(stakeType in $balancingFunctionsByStakeType)) {
    const BALANCING_STAKE_TYPES_CONF = getMatchGridTemplateByName('BALANCING_STAKE_TYPES_CONF');

    const key = Object.keys(BALANCING_STAKE_TYPES_CONF).find((key) => {
      return BALANCING_STAKE_TYPES_CONF[key].includes(stakeType);
    });

    $balancingFunctionsByStakeType[stakeType] = (
      (key && BALANCING_FUNCTION_MAP[key]) || defaultStakeTypeBalancer
    );
  }

  return $balancingFunctionsByStakeType[stakeType];
};

// --

/**
 * Create a composite slot id depending on the slot family configuration.
 *
 * @param {number} slotType
 * @param {number} stakeId
 * @return {string|number}
 */
export function composeGridLineSlotId(slotType, stakeId) {
  // noinspection JSRedundantSwitchStatement
  switch (getGridLineSlotFamilyConf(slotType)) {
    case SLOT_FAMILY_SINGLE:
      return `#${slotType}_${stakeId}`;

    default:
      return slotType;
  }
}

/**
 * Create a grid line stakes slot.
 *
 * @param {number|string} id – slot id
 * @param {number} type – slot type
 * @param {StakeEntity|Immutable.Map<string, *>} stake – the first (any) stake entry
 * @return {RecordOfSlotEntryType}
 */
export function createGridLineSlot(id, type, stake) {
  const family = getGridLineSlotFamilyConf(type);
  const { index, sequencer, params } = getGridLineSlotSequencingConf(type, stake);
  const slotName = getGridLineSlotNameConf(type, stake);

  return new SlotEntry({
    id,
    type,
    family,
    index,
    sequencer,
    params: params.reduce((map, key) => map.set(key, stake.getIn(['params', key])), new Map()),
    stakes: List.of(stake),
    getSlotName: getSlotName(slotName, stake),
  });
}

function getSlotName(slotName, stake) {
  return (lang) => {
    if (slotName && lang in slotName) {
      return slotName[lang];
    }

    return stake.getIn(['names', 'long']) || '--';
  };
}

// --

/**
 * Check whether the `state.params` satisfies of line's filtering conditions
 *
 * @param {object} lineStakeFilter
 * @param {object} stakeParams
 * @return {boolean}
 */
export function isFilteredLineStake(lineStakeFilter, stakeParams) {
  for (const key in lineStakeFilter) {
    if (stakeParams.get(key) !== lineStakeFilter[key]) {
      return false;
    }
  }

  return true;
}
